import React, { useState } from 'react';

// ============================================
// CALCULATORS — Shuffle bonus estimators (route: /calculators)
// Weekly + monthly bonus use the same community-known model:
//   base    = wager x house edge x 5%
//   lossback = net loss x 1% (weekly) / x 10% (monthly), losing periods only
// Estimates only — Shuffle's exact numbers can vary with VIP level/promos.
// ============================================

const EDGES = [
  { key: 'originals', label: 'Originals', icon: '🎲', edge: 0.01 },
  { key: 'mix', label: 'Mixed', icon: '🎭', edge: 0.02 },
  { key: 'slots', label: 'Slots', icon: '🎰', edge: 0.04 },
  { key: 'sports', label: 'Sports', icon: '⚽', edge: 0.045 }
];

const PERIODS = {
  weekly: {
    label: 'Weekly',
    icon: '📅',
    lossbackRate: 0.01,
    lossbackText: '1% of net loss',
    payout: 'Every Thursday · 11:00 UTC',
    presets: [1000, 5000, 10000, 25000, 50000]
  },
  monthly: {
    label: 'Monthly',
    icon: '🗓️',
    lossbackRate: 0.10,
    lossbackText: '10% of net loss',
    payout: 'First Friday of the month · 00:00 UTC',
    presets: [5000, 25000, 50000, 100000, 250000]
  }
};

const fmt = (n) => '$' + (Math.round(n * 100) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 });
const fmtShort = (n) => n >= 1000 ? '$' + (n / 1000) + 'K' : '$' + n;

export default function CalculatorsPage() {
  const [period, setPeriod] = useState('weekly');
  const [wagerInput, setWagerInput] = useState('');
  const [gameType, setGameType] = useState('slots');
  const [outcome, setOutcome] = useState('loss'); // loss | profit
  const [outcomeInput, setOutcomeInput] = useState('');

  const p = PERIODS[period];
  const edge = EDGES.find(e => e.key === gameType);
  const wager = Math.max(0, parseFloat(wagerInput.replace(/[$,\s]/g, '')) || 0);
  const outcomeAmt = Math.max(0, parseFloat(outcomeInput.replace(/[$,\s]/g, '')) || 0);

  const baseBonus = wager * edge.edge * 0.05;
  const lossback = outcome === 'loss' ? outcomeAmt * p.lossbackRate : 0;
  const total = baseBonus + lossback;
  const hasInput = wager > 0;

  const label = { fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '2px', color: '#8b8b96', display: 'block', marginBottom: '0.55rem' };
  const inputWrap = { position: 'relative' };
  const dollarPrefix = { position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#a855f7', fontWeight: 800, fontSize: '1.05rem', pointerEvents: 'none' };
  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '0.85rem 1rem 0.85rem 2.1rem', background: 'rgba(255,255,255,0.045)', border: '1px solid rgba(139,92,246,0.25)', borderRadius: '12px', color: '#fff', fontSize: '1.05rem', fontWeight: 700, fontFamily: 'Outfit, sans-serif', outline: 'none', transition: 'border-color 0.15s ease' };

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: '1.8rem' }}>
        <h1 className="page-title">🧮 Calculators</h1>
        <p className="page-subtitle">
          Estimate your Shuffle <strong style={{ color: '#c084fc' }}>weekly</strong> and <strong style={{ color: '#c084fc' }}>monthly</strong> bonuses before they drop.
        </p>
      </div>

      <div style={{ maxWidth: '920px', margin: '0 auto' }}>
        {/* Segmented period control */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', padding: '0.3rem', gap: '0.3rem' }}>
            {Object.entries(PERIODS).map(([key, cfg]) => (
              <button
                key={key}
                onClick={() => setPeriod(key)}
                style={{
                  padding: '0.6rem 1.8rem', borderRadius: '10px', border: 'none', cursor: 'pointer',
                  fontWeight: 800, fontSize: '0.92rem', fontFamily: 'Outfit, sans-serif',
                  background: period === key ? 'linear-gradient(135deg, #a855f7, #7c3aed)' : 'transparent',
                  color: period === key ? '#fff' : '#8b8b96',
                  boxShadow: period === key ? '0 4px 18px rgba(168,85,247,0.35)' : 'none',
                  transition: 'all 0.2s ease'
                }}
              >
                {cfg.icon} {cfg.label} Bonus
              </button>
            ))}
          </div>
        </div>

        {/* Inputs + result, side by side on desktop */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.2rem', alignItems: 'stretch' }}>

          {/* Inputs card */}
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '18px', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* Wager */}
            <div>
              <span style={label}>💰 {p.label} wager</span>
              <div style={inputWrap}>
                <span style={dollarPrefix}>$</span>
                <input
                  value={wagerInput}
                  onChange={e => setWagerInput(e.target.value)}
                  placeholder={`Total wagered this ${period === 'weekly' ? 'week' : 'month'}`}
                  inputMode="decimal"
                  style={inputStyle}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.55rem', flexWrap: 'wrap' }}>
                {p.presets.map(v => (
                  <button
                    key={v}
                    onClick={() => setWagerInput(String(v))}
                    style={{
                      padding: '0.28rem 0.7rem', borderRadius: '999px', cursor: 'pointer',
                      fontSize: '0.72rem', fontWeight: 700, fontFamily: 'Outfit, sans-serif',
                      background: wager === v ? 'rgba(168,85,247,0.2)' : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${wager === v ? 'rgba(168,85,247,0.6)' : 'rgba(255,255,255,0.1)'}`,
                      color: wager === v ? '#d8b4fe' : '#8b8b96'
                    }}
                  >
                    {fmtShort(v)}
                  </button>
                ))}
              </div>
            </div>

            {/* Game type */}
            <div>
              <span style={label}>🎮 What did you mostly play?</span>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' }}>
                {EDGES.map(e => {
                  const active = gameType === e.key;
                  return (
                    <button
                      key={e.key}
                      onClick={() => setGameType(e.key)}
                      style={{
                        padding: '0.65rem 0.3rem', borderRadius: '12px', cursor: 'pointer',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.2rem',
                        fontFamily: 'Outfit, sans-serif',
                        background: active ? 'rgba(168,85,247,0.16)' : 'rgba(255,255,255,0.035)',
                        border: `1px solid ${active ? '#a855f7' : 'rgba(255,255,255,0.09)'}`,
                        boxShadow: active ? '0 0 14px rgba(168,85,247,0.25)' : 'none',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <span style={{ fontSize: '1.15rem' }}>{e.icon}</span>
                      <span style={{ fontSize: '0.76rem', fontWeight: 800, color: active ? '#fff' : '#a1a1aa' }}>{e.label}</span>
                      <span style={{ fontSize: '0.62rem', fontWeight: 600, color: active ? '#c4b5fd' : '#71717a' }}>~{(e.edge * 100).toLocaleString()}% edge</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Outcome */}
            <div>
              <span style={label}>📊 {p.label} result</span>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: outcome === 'loss' ? '0.55rem' : 0 }}>
                <button
                  onClick={() => setOutcome('loss')}
                  style={{
                    padding: '0.6rem', borderRadius: '12px', cursor: 'pointer', fontWeight: 800, fontSize: '0.85rem', fontFamily: 'Outfit, sans-serif',
                    background: outcome === 'loss' ? 'rgba(239,68,68,0.14)' : 'rgba(255,255,255,0.035)',
                    border: `1px solid ${outcome === 'loss' ? 'rgba(239,68,68,0.55)' : 'rgba(255,255,255,0.09)'}`,
                    color: outcome === 'loss' ? '#fca5a5' : '#8b8b96', transition: 'all 0.15s ease'
                  }}
                >
                  📉 Down (loss)
                </button>
                <button
                  onClick={() => setOutcome('profit')}
                  style={{
                    padding: '0.6rem', borderRadius: '12px', cursor: 'pointer', fontWeight: 800, fontSize: '0.85rem', fontFamily: 'Outfit, sans-serif',
                    background: outcome === 'profit' ? 'rgba(34,197,94,0.14)' : 'rgba(255,255,255,0.035)',
                    border: `1px solid ${outcome === 'profit' ? 'rgba(34,197,94,0.55)' : 'rgba(255,255,255,0.09)'}`,
                    color: outcome === 'profit' ? '#4ade80' : '#8b8b96', transition: 'all 0.15s ease'
                  }}
                >
                  📈 Up (profit)
                </button>
              </div>
              {outcome === 'loss' && (
                <div style={inputWrap}>
                  <span style={{ ...dollarPrefix, color: '#f87171' }}>$</span>
                  <input
                    value={outcomeInput}
                    onChange={e => setOutcomeInput(e.target.value)}
                    placeholder="Amount lost"
                    inputMode="decimal"
                    style={{ ...inputStyle, borderColor: 'rgba(239,68,68,0.25)' }}
                  />
                </div>
              )}
              {outcome === 'profit' && (
                <p style={{ margin: '0.55rem 0 0', fontSize: '0.74rem', color: '#71717a' }}>
                  Profitable {period === 'weekly' ? 'weeks' : 'months'} still get the base bonus — just no lossback.
                </p>
              )}
            </div>
          </div>

          {/* Result card */}
          <div style={{
            borderRadius: '18px', padding: '1.5rem', display: 'flex', flexDirection: 'column',
            background: hasInput
              ? 'linear-gradient(160deg, rgba(168,85,247,0.14), rgba(124,58,237,0.05) 55%, rgba(251,191,36,0.06))'
              : 'rgba(255,255,255,0.02)',
            border: `1px solid ${hasInput ? 'rgba(168,85,247,0.45)' : 'rgba(255,255,255,0.07)'}`,
            boxShadow: hasInput ? '0 8px 40px rgba(168,85,247,0.15)' : 'none',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '1rem 0' }}>
              <div style={{ ...label, textAlign: 'center' }}>Estimated {p.label} bonus</div>
              {hasInput ? (
                <div style={{
                  fontSize: '3.2rem', fontWeight: 900, lineHeight: 1.1,
                  background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 4px 18px rgba(251,191,36,0.25))'
                }}>
                  {fmt(total)}
                </div>
              ) : (
                <div style={{ color: '#52525b', fontSize: '0.95rem', padding: '0.8rem 0' }}>
                  Enter your {period === 'weekly' ? 'weekly' : 'monthly'} wager to see your estimate ✨
                </div>
              )}
            </div>

            {hasInput && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0.55rem 0.8rem', background: 'rgba(0,0,0,0.25)', borderRadius: '10px' }}>
                  <span style={{ fontSize: '0.8rem', color: '#d4d4d8' }}>
                    Base bonus
                    <span style={{ display: 'block', fontSize: '0.66rem', color: '#71717a' }}>{fmt(wager)} × {(edge.edge * 100).toLocaleString()}% edge × 5%</span>
                  </span>
                  <strong style={{ color: '#fff', fontSize: '0.95rem' }}>{fmt(baseBonus)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '0.55rem 0.8rem', background: 'rgba(0,0,0,0.25)', borderRadius: '10px' }}>
                  <span style={{ fontSize: '0.8rem', color: '#d4d4d8' }}>
                    Lossback
                    <span style={{ display: 'block', fontSize: '0.66rem', color: '#71717a' }}>
                      {outcome === 'loss' ? p.lossbackText : 'no lossback on profitable periods'}
                    </span>
                  </span>
                  <strong style={{ color: lossback > 0 ? '#4ade80' : '#71717a', fontSize: '0.95rem' }}>{lossback > 0 ? '+' : ''}{fmt(lossback)}</strong>
                </div>
              </div>
            )}

            <div style={{
              marginTop: '1rem', padding: '0.6rem 0.9rem', borderRadius: '10px', textAlign: 'center',
              background: 'rgba(251,191,36,0.07)', border: '1px solid rgba(251,191,36,0.2)',
              fontSize: '0.76rem', fontWeight: 600, color: '#fbbf24'
            }}>
              ⏰ {p.payout} · no wagering requirement
            </div>
          </div>
        </div>

        <p style={{ fontSize: '0.74rem', color: '#71717a', textAlign: 'center', lineHeight: 1.6, marginTop: '1.3rem' }}>
          Estimates based on the community-known formula (theoretical loss × 5% + lossback). Shuffle's exact
          payout can vary with your VIP level and active promos — treat this as a ballpark, not a promise.
        </p>
      </div>
    </div>
  );
}
